/**
 * VETRO: match product image URLs to a variant color option even when Shopify renames files
 * (e.g. black_1.jpg → brown_16_uuid.png). Handles compound names like "Light Blue", "Dark Green",
 * optional homophones (Lloyd→light, Dork→dark). Multi-word names like Baby Blue require every
 * word in the filename (not generic blue_… alone). Plain "Blue" is hard-excluded from baby-blue
 * filenames (baby_blue, babyblue, …) so Blue and Baby Blue do not share the same gallery.
 * Used by vetro-color-gallery-filter, vetro-collection-card-color-gallery, and vetro-product-variant-picker.
 */
(function (global) {
  'use strict';

  /**
   * True “modifier + hue” first words only (light_blue, dark_green, off_white).
   * Do NOT list words that start a fixed color name (baby, powder, ice, sky, midnight, …)
   * or “Baby Blue” is parsed as prefix baby + hue blue and wrong gallery images match.
   */
  var COLOR_PREFIX_WORDS = {
    light: true,
    dark: true,
    pale: true,
    bright: true,
    deep: true,
    muted: true,
    dusty: true,
    warm: true,
    cool: true,
    soft: true,
    rich: true,
    matte: true,
    heather: true,
    ultra: true,
    extra: true,
    faded: true,
    sheer: true,
    off: true,
    pastel: true,
    antique: true,
    mid: true
  };

  /** First-token typos / homophones → canonical prefix word for an alternate match string */
  var PREFIX_HOMOPHONE_ALIASES = {
    lloyd: 'light',
    dork: 'dark'
  };

  function normalizeColor(str) {
    if (!str || typeof str !== 'string') return '';
    return String(str)
      .trim()
      .toLowerCase()
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Filename / URL is clearly a Baby Blue asset (not plain Blue). */
  function isBabyBlueAssetStemOrUrl(stem, raw) {
    var s = String(stem || '').toLowerCase();
    var r = String(raw || '').toLowerCase();
    return (
      /baby[\s._-]*blue/.test(s) ||
      /babyblue/.test(s) ||
      /baby[\s._-]*blue/.test(r) ||
      /babyblue/.test(r)
    );
  }

  function escapeRegExp(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function isPrefixWord(token) {
    if (!token) return false;
    return !!(COLOR_PREFIX_WORDS[token] || Object.prototype.hasOwnProperty.call(PREFIX_HOMOPHONE_ALIASES, token));
  }

  /**
   * Consume consecutive known prefix tokens from the start (e.g. Bright + Light + Blue → rest [blue]).
   */
  function stripLeadingPrefixes(parts) {
    var i = 0;
    while (i < parts.length && isPrefixWord(parts[i])) i++;
    return { prefixCount: i, rest: parts.slice(i) };
  }

  /** Alternate option spelling for URL matching (e.g. Lloyd Green → also try Light Green). */
  function buildColorMatchVariants(colorNorm) {
    var out = [];
    var seen = Object.create(null);
    function add(s) {
      if (!s || seen[s]) return;
      seen[s] = true;
      out.push(s);
    }
    add(colorNorm);
    var parts = colorNorm.split(' ').filter(Boolean);
    if (parts.length >= 2 && PREFIX_HOMOPHONE_ALIASES[parts[0]]) {
      add(PREFIX_HOMOPHONE_ALIASES[parts[0]] + ' ' + parts.slice(1).join(' '));
    }
    return out;
  }

  function pathLower(url) {
    var s = String(url || '').split('?')[0];
    try {
      s = decodeURIComponent(s);
    } catch (e) {
      /* ignore */
    }
    return s.toLowerCase();
  }

  function filenameStemLower(url) {
    var path = pathLower(url);
    var m = path.match(/\/([^/?]+)$/);
    var base = m ? m[1] : path;
    return base.replace(/\.[^.]+$/, '');
  }

  function extractLegacyColorStem(url) {
    var stem = filenameStemLower(url);
    var match = stem.match(/^([a-zA-Z\s\-]+)(?:_\d+)?$/);
    if (match && match[1]) return normalizeColor(match[1]);
    return '';
  }

  function tokenAppearsInStem(stemLower, token) {
    if (!stemLower || !token || token.length < 3) return false;
    if (stemLower.indexOf(token) === -1) return false;
    var re = new RegExp('(^|[^a-z0-9])' + escapeRegExp(token) + '([^a-z0-9]|$)', 'i');
    return re.test(stemLower);
  }

  function phraseMatches(parts, raw) {
    if (!parts || parts.length < 2) return false;
    var phraseRe = new RegExp(parts.map(escapeRegExp).join('[\\s._\\-0-9]+'), 'i');
    return phraseRe.test(raw);
  }

  function looseTokenMatch(parts, stem, raw) {
    for (var i = 0; i < parts.length; i++) {
      var t = parts[i];
      if (t.length < 3) continue;
      if (tokenAppearsInStem(stem, t)) return true;
      if (t.length >= 5 && raw.indexOf(t) !== -1) return true;
    }
    return false;
  }

  /**
   * Named multi-word colors (Baby Blue, Navy Blue): every significant word must appear in the
   * filename stem so assets that are only generic "blue_…" do not match Baby Blue.
   */
  function multiWordNamedColorMatch(parts, stem, raw) {
    var need = 0;
    var hit = 0;
    for (var i = 0; i < parts.length; i++) {
      var t = parts[i];
      if (t.length < 3) continue;
      need++;
      if (tokenAppearsInStem(stem, t)) hit++;
      else if (t.length >= 5 && raw.indexOf(t) !== -1) hit++;
    }
    return need > 0 && hit === need;
  }

  function variantMatches(url, colorNorm, raw, stem, stemCompact) {
    var legacy = extractLegacyColorStem(url);
    if (legacy && legacy === colorNorm) return true;

    var parts = colorNorm.split(' ').filter(Boolean);
    var stripped = stripLeadingPrefixes(parts);
    var isCompoundPrefix =
      stripped.prefixCount > 0 && stripped.rest.length > 0 && parts.length >= 2;

    // Hardcode: plain "blue" must never match baby-blue filenames (substring "blue" in "babyblue", etc.)
    if (colorNorm === 'blue' && isBabyBlueAssetStemOrUrl(stem, raw)) {
      return false;
    }

    var colorCompact = colorNorm.replace(/\s+/g, '');
    if (colorCompact.length >= 2 && (raw.indexOf(colorCompact) !== -1 || stemCompact.indexOf(colorCompact) !== -1)) {
      return true;
    }

    if (parts.length > 1 && phraseMatches(parts, raw)) return true;

    if (isCompoundPrefix) {
      return false;
    }

    if (parts.length >= 2) {
      return multiWordNamedColorMatch(parts, stem, raw);
    }

    return looseTokenMatch(parts, stem, raw);
  }

  /**
   * @param {string} url - Image URL (any width / CDN path)
   * @param {string} colorOptionValue - Shopify option value, e.g. "Light Blue", "Dork Green"
   */
  function urlMatchesColor(url, colorOptionValue) {
    var colorNorm = normalizeColor(colorOptionValue);
    if (!colorNorm) return false;

    var raw = pathLower(url);
    if (!raw) return false;

    var stem = filenameStemLower(url);
    var stemCompact = stem.replace(/[\s._-]/g, '');

    var variants = buildColorMatchVariants(colorNorm);
    for (var v = 0; v < variants.length; v++) {
      if (variantMatches(url, variants[v], raw, stem, stemCompact)) return true;
    }
    return false;
  }

  global.vetroColorImageMatch = {
    normalizeColor: normalizeColor,
    extractLegacyColorStem: extractLegacyColorStem,
    urlMatchesColor: urlMatchesColor,
    /** Exposed for tests / theme extensions */
    buildColorMatchVariants: buildColorMatchVariants,
    isBabyBlueAssetStemOrUrl: isBabyBlueAssetStemOrUrl
  };
})(typeof window !== 'undefined' ? window : this);
